import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Rocket, X, ArrowRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DISMISS_KEY = "josfit_banner_dismissed";

function daysUntil(dateStr) {
  if (!dateStr) return 0;
  const target = new Date(`${dateStr}T00:00:00`);
  if (isNaN(target.getTime())) return 0;
  const diff = Math.ceil((target.getTime() - Date.now()) / (1000 * 60 * 60 * 24));
  return diff > 0 ? diff : 0;
}

export const AnnouncementBanner = () => {
  const [banner, setBanner] = useState(null);
  const [visible, setVisible] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    let mounted = true;
    axios
      .get(`${API}/banner`)
      .then(({ data }) => {
        if (!mounted) return;
        const stamp = data.updated_at || data.message;
        const dismissed = sessionStorage.getItem(DISMISS_KEY) === stamp;
        setBanner(data);
        setVisible(Boolean(data.enabled) && !dismissed);
      })
      .catch(() => {});
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    const setVar = () => {
      const h = visible && ref.current ? ref.current.offsetHeight : 0;
      document.documentElement.style.setProperty("--banner-h", `${h}px`);
    };
    setVar();
    window.addEventListener("resize", setVar);
    return () => {
      window.removeEventListener("resize", setVar);
      document.documentElement.style.setProperty("--banner-h", "0px");
    };
  }, [visible, banner]);

  if (!visible || !banner) return null;

  const days = daysUntil(banner.launch_date);

  const dismiss = () => {
    sessionStorage.setItem(DISMISS_KEY, banner.updated_at || banner.message);
    setVisible(false);
  };

  return (
    <div
      ref={ref}
      data-testid="announcement-banner"
      className="fixed top-0 inset-x-0 z-[60] bg-gradient-to-r from-emerald-400 via-emerald-300 to-teal-300 text-emerald-950 shadow-lg shadow-emerald-500/20"
    >
      <div className="relative mx-auto flex max-w-7xl flex-wrap items-center justify-center gap-x-3 gap-y-1 px-10 py-2.5 text-center">
        <Rocket className="h-4 w-4 shrink-0" />
        <span data-testid="announcement-banner-message" className="text-sm font-semibold">
          {banner.message}
        </span>
        {days > 0 && (
          <span
            data-testid="announcement-banner-countdown"
            className="rounded-full bg-emerald-950/15 px-2.5 py-0.5 text-xs font-extrabold tracking-wide"
          >
            J-{days}
          </span>
        )}
        {banner.cta_url && banner.cta_label && (
          <a
            href={banner.cta_url}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="announcement-banner-cta"
            className="inline-flex items-center gap-1 rounded-full bg-emerald-950 px-3.5 py-1 text-xs font-bold text-emerald-50 transition-colors hover:bg-emerald-900"
          >
            {banner.cta_label}
            <ArrowRight className="h-3 w-3" />
          </a>
        )}
        <button
          onClick={dismiss}
          data-testid="announcement-banner-close"
          aria-label="Fermer l'annonce"
          className="absolute right-3 top-1/2 -translate-y-1/2 text-emerald-950/70 transition-colors hover:text-emerald-950"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
};
